import { TfiClose } from "react-icons/tfi";
import {
  ShopComIcon,
  IsotonixIcon,
  Nutrametrix,
  LayeredIcon,
  LumiereIcon,
  MotivesIcon,
  TLS,
} from "../../../../assets/icons";
import "./style.css";
import { useDispatch } from "react-redux";
import { getShopSiteData, clearProductData } from "../../interaction.actions";
import useAppAccessor from "../../../../hooks/useappAccessor";
import { useState } from "react";

const STORAGE_KEY = 'selected_market_site';

const ConfirmModal = ({ isOpen, onConfirm, onCancel, siteName }: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  siteName: string;
}) => {
  if (!isOpen) return null;

  const marketName = siteName === "nfr" ? "NFR Market (USD)" : "OTG Market (CAD)";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-[#181818] rounded-lg p-5 max-w-xs w-full mx-4 border border-gray-700">
        <h3 className="text-base font-medium text-white mb-3">
          Confirm Market Switch
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          Are you sure you want to switch to {marketName}?, the product list will be cleared.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-1.5 text-sm bg-white text-black hover:bg-gray-200 rounded-md transition-colors"
          >
            Switch
          </button>
        </div>
      </div>
    </div>
  );
};

const ShopSite = (props: any) => {
  const dispatch = useDispatch();
  const { getUserData } = useAppAccessor();
  const { language, user } = getUserData();
  const { publicUserData } = user;
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingSiteName, setPendingSiteName] = useState<string | null>(null);

  const handleSiteSelection = (siteName: string) => {
    // Get the currently saved site from sessionStorage
    const savedSite = sessionStorage.getItem(STORAGE_KEY);
    
    // If no site is saved or selecting a different site, show confirmation
    if (savedSite !== siteName) {
      setPendingSiteName(siteName);
      setShowConfirm(true);
    } else {
      // If selecting the same site, just open modal without clearing data
      dispatch(getShopSiteData(siteName));
      props.handleOpenModal();
    }
  };

  const handleConfirm = () => {
    if (pendingSiteName) {
      // Save the selection to sessionStorage
    const savedSite = sessionStorage.getItem(STORAGE_KEY);
    if(savedSite || savedSite !== pendingSiteName)
      dispatch(clearProductData());

      sessionStorage.setItem(STORAGE_KEY, pendingSiteName);
      
      // Clear data and proceed with site switch
      dispatch(getShopSiteData(pendingSiteName));
      props.handleOpenModal();
    }
    setShowConfirm(false);
    setPendingSiteName(null);
  };

  // Helper function to get button styles based on current selection
  const getButtonStyles = (siteName: string) => {
    const savedSite = sessionStorage.getItem(STORAGE_KEY);
    const baseStyles = "w-full flex items-center justify-between px-8 py-4 rounded-xl transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl border";
    
    if (savedSite === siteName) {
      return `${baseStyles} bg-gray-100 border-gray-300 text-gray-900`;
    }
    return `${baseStyles} bg-white hover:bg-gray-50 text-gray-800 border-gray-100`;
  };

  const guideText = () => {
    if(publicUserData?.company_name === "tranont") {
      return language["guide_select_site_v2"];
    }
    return language["guide_select_site"];
  }

  if (publicUserData?.company_name === "tranont") {
    return (
      <>
        <ConfirmModal
          isOpen={showConfirm}
          onConfirm={handleConfirm}
          onCancel={() => {
            setShowConfirm(false);
            setPendingSiteName(null);
          }}
          siteName={pendingSiteName || ""}
        />
        <div className="relative w-full">
          <div
            className="h-full w-full flex flex-col listContainerShop"
            style={{ backgroundColor: "#181818" }}
          >
            <div className="overflow-y-auto containerbottomSheet">
              <div className="flex w-full justify-between px-3 h-10 items-center">
                <div className="h-25 w-80 mt-6 ml-7 text-white text-3xl text-bold-800">
                  {language["select_site"]}
                </div>
                <TfiClose
                  color="#DEDEDE"
                  size={20}
                  cursor="pointer"
                  onClick={() => {
                    props.handleCloseClick();
                  }}
                />
              </div>
              <p
                className="text-14 text-white mt-10 ml-10"
                style={{ marginRight: "1.5rem" }}
              >
                {guideText()}
              </p>
              <div className="mt-[5%]">
                <div
                  style={{ marginTop: "1.2rem" }}
                  className="w-[80%] ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl bg-white shadow-lg"
                >
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => handleSiteSelection("nfr")}
                      className={getButtonStyles("nfr")}
                    >
                      <div className="flex flex-col items-start">
                        <span className="text-lg font-semibold">NFR Market</span>
                        <span className="text-sm text-gray-500">Not-For-Resale (USD)</span>
                      </div>
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div
                  style={{ marginTop: "1.2rem" }}
                  className="w-[80%] ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl bg-white shadow-lg"
                >
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => handleSiteSelection("otg")}
                      className={getButtonStyles("otg")}
                    >
                      <div className="flex flex-col items-start">
                        <span className="text-lg font-semibold">OTG Market</span>
                        <span className="text-sm text-gray-500">On-The-Ground (CAD)</span>
                      </div>
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="relative w-full">
      <div
        className="h-full w-full flex flex-col listContainerShop"
        style={{ backgroundColor: "#181818" }}
      >
        <div className="overflow-y-auto containerbottomSheet">
          <div className="flex w-full justify-between px-3 h-10 items-center">
            <div className="h-25 w-80 mt-6 ml-7 text-white text-3xl text-bold-800">
              {language["select_site"]}
            </div>
            <TfiClose
              color="#DEDEDE"
              size={20}
              cursor="pointer"
              onClick={() => {
                props.handleCloseClick();
              }}
            />
          </div>
          <p
            className="text-14 text-white mt-10 ml-10"
            style={{ marginRight: "1.5rem" }}
          >
            {language["guide_select_site"]}
          </p>
          <div className="mt-[5%]">
            <div
              onClick={() => {
                props.handleOpenModal();
                dispatch(getShopSiteData("shopcomesite"));
              }}
              className="w-[80%] pl-5 pr-5 ml-12 position-block mt-20 h-18 mb-3 border border-white border-opacity-27 rounded-2xl  bg-white shadow-lg"
            >
              <div className="flex items-center justify-center">
                {" "}
                <img className="h-7 mt-2 mb-2 w-[160px]" src={ShopComIcon} />
              </div>
            </div>
            {publicUserData.user_lhp === 1 && (
              <div
                onClick={() => {
                  props.handleOpenModal();
                  dispatch(getShopSiteData("nutraMetrix"));
                }}
                style={{ marginTop: "1.2rem" }}
                className="w-[80%] pl-5 pr-5 ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl bg-white shadow-lg"
              >
                <div className="flex items-center justify-center">
                  {" "}
                  <img className="h-7 mt-2 mb-2 w-[160px]" src={Nutrametrix} />
                </div>
              </div>
            )}

            <div
              onClick={() => {
                props.handleOpenModal();
                dispatch(getShopSiteData("isotonix"));
              }}
              style={{ marginTop: "1.2rem" }}
              className="w-[80%] pl-5 pr-5 ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl bg-white shadow-lg"
            >
              <div className="flex items-center justify-center">
                {" "}
                <img className="h-7 mt-2 mb-2 w-[160px]" src={IsotonixIcon} />
              </div>
            </div>
            <div
              onClick={() => {
                props.handleOpenModal();
                dispatch(getShopSiteData("layered"));
              }}
              style={{ marginTop: "1.2rem" }}
              className="w-[80%] pl-5 pr-5 ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl bg-white	 shadow-lg"
            >
              <div className="flex items-center justify-center">
                {" "}
                <img className="h-7 mt-2 mb-2 w-[160px]" src={LayeredIcon} />
              </div>
            </div>
            <div
              onClick={() => {
                props.handleOpenModal();
                dispatch(getShopSiteData("lumiere"));
              }}
              style={{ marginTop: "1.2rem" }}
              className="w-[80%] pl-5 pr-5 ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl  bg-white shadow-lg"
            >
              <div className="flex items-center justify-center">
                {" "}
                <img className="h-7 mt-2 mb-2 w-[160px]" src={LumiereIcon} />
              </div>
            </div>
            <div
              onClick={() => {
                props.handleOpenModal();
                dispatch(getShopSiteData("motives"));
              }}
              style={{ marginTop: "1.2rem" }}
              className="w-[80%] pl-5 pr-5 ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl  bg-white shadow-lg"
            >
              <div className="flex items-center justify-center">
                {" "}
                <img className="h-7 mt-2 mb-2 w-[160px]" src={MotivesIcon} />
              </div>
            </div>
            <div
              onClick={() => {
                props.handleOpenModal();
                dispatch(getShopSiteData("tls"));
              }}
              style={{ marginTop: "1.2rem" }}
              className="w-[80%] pl-5 pr-5 ml-12 position-block mt-10 h-18 mb-3 border border-white border-opacity-27 rounded-2xl  bg-white shadow-lg"
            >
              <div className="flex items-center justify-center">
                {" "}
                <img className="h-7 mt-2 mb-2" src={TLS} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopSite;
